import { Flex } from '@chakra-ui/react';
import React from 'react';

import { CaseIdentifier } from '../../molecules/CaseIdentifier';
import { ShortcutStatusBar } from '../../molecules/ShortcutStatusBar';
import {
  FOOTER_H,
  SHELL_PAD,
  STRIP_BG,
  STRIP_BORDER
} from '../../styles/tokens';

/**
 * Footer organism -- composes the two footer molecules into a single row, `ShortcutStatusBar` at the
 * start (left) and `CaseIdentifier` at the end (right). Pure composition, no data logic of its own --
 * each molecule independently reads its own config slice via `useCodingConfig()`, so `PageFooter`
 * itself takes no props and reads no context, mirroring `HeaderBar`'s precedent.
 *
 * Renders a SEMANTIC `<footer>` element (`as="footer"`), never an explicit `role="contentinfo"` prop,
 * because CMx already mounts this shell inside its own `<main>`, where a second, nested `contentinfo`
 * landmark would be invalid. A bare `<footer>` picks up the correct landmark role implicitly wherever
 * it happens to sit in the DOM (browsers only grant `contentinfo` when it is NOT nested inside
 * `main`/`article`/`aside`/`nav`/`section`), so this component never needs to know which context it is
 * mounted in.
 *
 * CHROME (styles/tokens.ts): a thin light full-width strip below the two pane cards, reusing the same
 * `STRIP_BG`/`STRIP_BORDER`/`FOOTER_H` family every other in-card strip uses (the mockup reads this
 * outer footer as a hair lighter than the in-card strips, but close enough to treat as the same token
 * pending the browser-tuning pass rather than adding a near-duplicate color). Horizontal padding uses
 * `SHELL_PAD` (the same shell-level padding rhythm `CodingScreenTemplate`'s root uses), not a locally
 * hard-coded `3`.
 */
export const PageFooter: React.FC = () => (
  <Flex
    as="footer"
    justify="space-between"
    align="center"
    flexShrink={0}
    h={FOOTER_H}
    minH={FOOTER_H}
    px={SHELL_PAD}
    bg={STRIP_BG}
    borderTop={`1px solid ${STRIP_BORDER}`}
  >
    <ShortcutStatusBar />
    <CaseIdentifier />
  </Flex>
);
